import React, { useCallback, useEffect, useState } from 'react';
import { Search, Check, X, Ban, ChevronLeft, ChevronRight, Loader2, Eye } from 'lucide-react';
import dayjs from 'dayjs';
import { adminApi, adminErrorMsg } from '../../admin/adminApiClient';
import { usePermissions } from '../../admin/usePermissions';
import { toast } from 'sonner';

const STATUSES = ['all', 'pending', 'approved', 'rejected', 'suspended'];
const STATUS_STYLE = {
  pending: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-600',
  suspended: 'bg-gray-100 text-gray-600',
};

export default function AdminOrganizersPage() {
  const { has } = usePermissions();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [approveConfirm, setApproveConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { limit, offset };
      if (q) params.q = q;
      if (status !== 'all') params.status = status;
      const r = await adminApi.get('/organizers', { params });
      setItems(r.data?.items || r.data || []);
      setTotal(r.data?.total ?? (r.data?.length || 0));
    } catch (e) { toast.error(adminErrorMsg(e)); }
  }, [q, status, offset]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  const openDetail = async (o) => {
    setDetail(o);
    setDetailLoading(true);
    try {
      const r = await adminApi.get(`/organizers/${o.id || o.user_id}`);
      setDetail(r.data);
    } catch { /* ignore */ }
    finally { setDetailLoading(false); }
  };

  const approve = async (id) => {
    setBusy(true);
    try {
      await adminApi.post(`/organizers/${id}/approve`);
      toast.success('Organizer approved');
      setDetail(null); setApproveConfirm(null);
      await load();
    } catch (e) { toast.error(adminErrorMsg(e)); }
    finally { setBusy(false); }
  };

  const reject = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    setBusy(true);
    try {
      await adminApi.post(`/organizers/${id}/reject`, { reason });
      toast.success('Organizer rejected');
      setDetail(null);
      await load();
    } catch (e) { toast.error(adminErrorMsg(e)); }
    finally { setBusy(false); }
  };

  const suspend = async (id) => {
    if (!window.confirm('Suspend this organizer?')) return;
    setBusy(true);
    try {
      await adminApi.post(`/organizers/${id}/suspend`);
      toast.success('Organizer suspended');
      setDetail(null);
      await load();
    } catch (e) { toast.error(adminErrorMsg(e)); }
    finally { setBusy(false); }
  };

  const pages = Math.ceil(total / limit);
  const page = Math.floor(offset / limit) + 1;

  return (
    <div data-testid="admin-organizers-page">
      <h1 className="text-xl font-black text-gray-900 mb-4">Organizers</h1>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400" />
          <input data-testid="org-search" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} placeholder="Search organizers..." className="py-2 text-sm bg-transparent focus:outline-none flex-1" />
        </div>
        <select data-testid="org-status-filter" value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }} className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700">
          {STATUSES.map((s) => <option key={s} value={s}>{s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-magenta" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="org-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Organization</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Applied</th>
                  <th className="text-right px-4 py-3 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((o) => {
                  const oid = o.id || o.user_id;
                  const st = o.organizer_status || o.status || 'pending';
                  return (
                    <tr key={oid} data-testid={`org-row-${oid}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-semibold text-gray-900">{o.organization_name || o.full_name}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{o.contact_email || o.email}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${STATUS_STYLE[st] || 'bg-gray-100 text-gray-600'}`}>{st.toUpperCase()}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{dayjs(o.created_at).format('MMM D, YYYY')}</td>
                      <td className="px-4 py-3 text-right flex items-center justify-end gap-1">
                        <button data-testid={`org-detail-${oid}`} onClick={() => openDetail(o)} className="p-1.5 rounded hover:bg-gray-100" title="Details"><Eye className="w-4 h-4 text-gray-500" /></button>
                        {st === 'pending' && has('organizers.approve') && (
                          <>
                            <button data-testid={`org-approve-${oid}`} onClick={() => setApproveConfirm({ id: oid, name: o.organization_name || o.full_name })} className="p-1.5 rounded hover:bg-emerald-50" title="Approve"><Check className="w-4 h-4 text-emerald-600" /></button>
                            <button data-testid={`org-reject-${oid}`} onClick={() => reject(oid)} className="p-1.5 rounded hover:bg-red-50" title="Reject"><X className="w-4 h-4 text-red-500" /></button>
                          </>
                        )}
                        {st === 'approved' && has('organizers.suspend') && (
                          <button data-testid={`org-suspend-${oid}`} onClick={() => suspend(oid)} className="p-1.5 rounded hover:bg-amber-50" title="Suspend"><Ban className="w-4 h-4 text-amber-600" /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {items.length === 0 && <p className="text-center text-sm text-gray-400 py-10">No organizers found</p>}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">{total} total · Page {page}/{pages}</p>
          <div className="flex gap-1">
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
            <button disabled={page >= pages} onClick={() => setOffset(offset + limit)} className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDetail(null)} />
          <div className="relative bg-white h-full w-full max-w-md overflow-y-auto shadow-xl p-6" data-testid="org-detail-drawer">
            <button onClick={() => setDetail(null)} className="mb-4 p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
            {detailLoading ? <Loader2 className="w-6 h-6 animate-spin text-brand-magenta mx-auto mt-10" /> : (
              <>
                <h2 className="text-lg font-black text-gray-900">{detail.organization_name || detail.full_name}</h2>
                <p className="text-sm text-gray-500 mb-4">{detail.contact_email || detail.email}</p>
                <div className="space-y-3 text-sm">
                  <DetailItem label="Contact person" value={detail.contact_person || detail.full_name} />
                  <DetailItem label="Phone" value={detail.contact_phone || detail.phone} />
                  <DetailItem label="Description" value={detail.business_description} />
                  <DetailItem label="Website" value={detail.website} />
                  <DetailItem label="Status" value={(detail.organizer_status || detail.status || '—').toUpperCase()} />
                  <DetailItem label="Applied" value={dayjs(detail.created_at).format('MMM D, YYYY h:mm A')} />
                </div>
                <div className="flex flex-col gap-2 mt-6">
                  {(detail.organizer_status || detail.status) === 'pending' && (
                    <>
                      <button data-testid="drawer-approve-btn" disabled={busy} onClick={() => setApproveConfirm({ id: detail.id || detail.user_id, name: detail.organization_name || detail.full_name })} className="py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 transition-colors">Approve</button>
                      <button data-testid="drawer-reject-btn" disabled={busy} onClick={() => reject(detail.id || detail.user_id)} className="py-2.5 rounded-lg bg-red-50 text-red-600 text-sm font-bold hover:bg-red-100 disabled:opacity-50 transition-colors">Reject</button>
                    </>
                  )}
                  {(detail.organizer_status || detail.status) === 'approved' && (
                    <button data-testid="drawer-suspend-btn" disabled={busy} onClick={() => suspend(detail.id || detail.user_id)} className="py-2.5 rounded-lg bg-amber-50 text-amber-700 text-sm font-bold hover:bg-amber-100 disabled:opacity-50 transition-colors">Suspend</button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Approve Confirmation Dialog */}
      {approveConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setApproveConfirm(null)} />
          <div className="relative bg-white rounded-xl w-full max-w-sm p-6 mx-4 shadow-xl" data-testid="org-approve-confirm-dialog">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center"><Check className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Approve Organizer</h3>
                <p className="text-xs text-gray-500 mt-0.5">Approve <strong>{approveConfirm.name}</strong> as an Organizer? This will grant them access to Organizer tools.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid="org-approve-cancel-btn" onClick={() => setApproveConfirm(null)} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
              <button data-testid="org-approve-confirm-btn" disabled={busy} onClick={() => approve(approveConfirm.id)}
                className="flex-1 py-2.5 rounded-lg bg-brand-magenta text-white text-sm font-bold disabled:opacity-50 hover:bg-pink-700 transition-colors">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailItem({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm text-gray-900 font-medium mt-0.5">{value}</p>
    </div>
  );
}
